import React, { useEffect, useState } from 'react';
import { List } from 'immutable';
import PropTypes from 'prop-types';
import { Col, Row, Table } from 'react-bootstrap';
import t from '../../lib/I18n';
import Icon from '../general/Icon';
import Checkbox from '../form/Checkbox';
import trainTime from '../../lib/TrainTime';
import { durationString } from '../../helpers/Durations';
import BAlert, { bAlertType } from '../../components/general/BAlert';
import config from '../../config';
import Search from '../../records/Search';
import LoadingStore from '../../stores/LoadingStore';
import connectToStores from '../../helpers/connectToStores';
import useScreenDimensions from '../../hooks/useScreenDimensions';

const renderNoJourneySearchResults = (loading) => (
    <BAlert className="margin-bottom--sm margin-top--sm" type={ loading ? bAlertType.SUCCESS : bAlertType.DANGER }>
        {t(`auto-dr.account.subscribe.journeys.modal.${loading ? 'loading-journeys' : 'no-compatible-journeys'}`)}
    </BAlert>
);

const AddJourneyModalTravelTable = ({
    businessDays,
    outwardJourneys,
    remainingJourneys,
    search,
    selectedJourneys,
    setDisabledSaveJourney,
    setNewTime,
    setSelectedJourneys,
    weekday,
    editingJourney,
    loading,
}) => {
    const maxJourneys = config.get('autoDelayRepayMaxJourneys');

    const [canLoadEarlierServices, setCanLoadEarlierServices] = useState(true);
    const [canLoadLaterServices, setCanLoadLaterServices] = useState(true);

    const selectedDays = weekday === 'weekday' ? businessDays.filter(day => day.checked) : [weekday];

    const journeysQuantity = (maxJourneys - remainingJourneys - (editingJourney ? (editingJourney.serviceTimes.length * editingJourney.daysOfWeek.length) : 0)) + (selectedJourneys ? (selectedJourneys.length * selectedDays.length) : 0);

    const { isMobileWidth } = useScreenDimensions();

    const handleJourney = journey => {
        if (!selectedJourneys.find(x => x.id === journey.id)) {
            setSelectedJourneys([...selectedJourneys, journey]);
        } else {
            setSelectedJourneys([...selectedJourneys].filter(x => x.id !== journey.id));
        }
    };

    useEffect(() => {
        if (!search.origin.nlc || !search.destination.nlc || !businessDays.some(day => day.checked) || journeysQuantity > maxJourneys || selectedJourneys.length < 1) {
            setDisabledSaveJourney(true);
        } else {
            setDisabledSaveJourney(false);
        }
        // eslint-disable-next-line
    }, [journeysQuantity, maxJourneys, selectedJourneys.length, search.origin, search.destination]);

    const searchTime = search.outboundDateTime.datetime;

    useEffect(() => {
        setCanLoadEarlierServices(searchTime.isSame(searchTime.clone().subtract(1, 'hours'), 'day'));
        setCanLoadLaterServices(searchTime.isSame(searchTime.clone().add(1, 'hours'), 'day'));
    }, [searchTime]);

    const loadEarlierOrLaterServices = async (isLater) => {
        if (isLater) {
            setNewTime(searchTime.add(1, 'hours'));
        } else {
            setNewTime(searchTime.subtract(1, 'hours'));
        }
    };

    const sortedJourneys = outwardJourneys.sort((a, b) => a.origin.time.scheduledTime.valueOf() - b.origin.time.scheduledTime.valueOf());

    const loadEarlierServices = (e) => {
        e.preventDefault();
        loadEarlierOrLaterServices();
    };

    const loadLaterServices = (e) => {
        e.preventDefault();
        loadEarlierOrLaterServices(true);
    };

    return (
        <React.Fragment>
            {outwardJourneys.size > 0 ? <React.Fragment>
                <div className="row margin-left--xs margin-right--xs">
                    {canLoadEarlierServices && <Col xs={12} className="journey-modal-travel-title margin-top--sm margin-bottom--xs">
                        {t('auto-dr.account.subscribe.journeys.modal.travel')}
                        <a
                            onClick={loadEarlierServices}
                            href="#"
                            className="service-pager pull-right"
                            aria-label={t('booking.fare.eligibleServicesModal.table.earlier.ariaLabel')}>
                            <span aria-hidden="true">
                                <i className="otrl otrl-chevron otrl-rotate-270" aria-hidden="true" />
                                <span className="sr-only">Show</span>
                                Earlier
                            </span>
                        </a>
                    </Col>}
                    <div className="journey-modal-travel-table-wrapper">
                        <Table xs={12} className="journey-modal-travel-table">
                            <thead>
                                <tr className="journey-modal-travel-table-title">
                                    <th className="journey-modal-travel-table-title-departing">
                                        {t('journey.modal.travel.table.departureTime')}
                                    </th>
                                    <th>{t('journey.modal.travel.table.from')}</th>
                                    <th>{t('journey.modal.travel.table.to')}</th>
                                    <th className="journey-modal-travel-table-duration" >{t('journey.modal.travel.table.duration')}</th>
                                    <th className="journey-modal-travel-table-title-changed">{t('journey.modal.travel.table.changes')}</th>
                                    <th className="journey-modal-travel-table-title-selected">
                                        {t(isMobileWidth ? 'journey.modal.travel.table.selected.mobile' : 'journey.modal.travel.table.selected')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedJourneys.map(journey => {
                                    const departing = trainTime(journey.origin.time.scheduledTime).format('HH:mm');
                                    const from = journey.origin.station.name;
                                    const to = journey.destination.station.name;
                                    const mobileFrom = journey.origin.station.crs;
                                    const mobileTo = journey.destination.station.crs;

                                    const duration = journey.getTripDuration();

                                    const { legs } = journey;

                                    const changes = legs && legs.size - 1;
                                    const fullDurationString = durationString({
                                        hours: duration._data.hours,
                                        minutes: duration._data.minutes,
                                    });
                                    const checked = selectedJourneys.find(x => x.id === journey.id);

                                    const ariaLabel = t('auto-dr.account.subscribe.journeys.modal.travel.from-to-aria-label', {
                                        departing,
                                        from,
                                        to,
                                        duration: `${fullDurationString.replace('m', 'minutes')}`,
                                        changes
                                    });

                                    return (
                                        <tr key={journey.id} className="journey-modal-travel-table-row">
                                            <td aria-hidden className="journey-modal-travel-table-departing journey-modal-travel-table-cell-bold">
                                            {departing}
                                            </td>
                                            <td aria-hidden className="journey-modal-travel-table-from journey-modal-travel-table-cell-bold">{isMobileWidth ? mobileFrom : from}
                                                <div className="journey-modal-travel-table-icon-wrap">
                                                    <Icon
                                                        icon="arrowback"
                                                        className="otrl-rotate-180 journey-modal-travel-table-icon"
                                                    />
                                                </div>
                                            </td>
                                            <td aria-hidden className="journey-modal-travel-table-cell-bold">{isMobileWidth ? mobileTo : to}</td>
                                            <td aria-hidden className="journey-modal-travel-table-duration journey-modal-travel-table-cell-bold">
                                                {fullDurationString}
                                            </td>
                                            <td aria-hidden className="journey-modal-travel-table-changes">{changes}</td>
                                            <td aria-hidden className="journey-modal-travel-table-select">
                                                {legs.size < 1 ? (
                                                    `No ${t('app.owner.short')} legs`
                                                ) : (
                                                    <Checkbox
                                                        className="journey-modal-table-checkbox"
                                                        onChange={() => handleJourney(journey)}
                                                        checked={checked}
                                                        aria-label={ariaLabel}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                    <Col xs={12} className="journey-modal-travel-selected margin-top--sm margin-bottom--xs">
                        <Row>
                            <Col xs={10} className="journey-modal-travel-selected-time">
                                {selectedJourneys && selectedJourneys.map((j) => (
                                    <div
                                        className="journey-card-time-col padding--vert-xs padding--horiz-xs margin-right--xs"
                                    >
                                        {j.origin.getTime().format('HH:mm')}
                                        <button
                                            onClick={() => handleJourney(j)}
                                            className="modal-header__close-button"
                                            aria-label={`Delete this journey: ${j.origin.getTime().format('HH:mm')}`}
                                        >
                                            <Icon icon="close" />
                                        </button>
                                    </div>
                                ))}
                                {journeysQuantity >= (config.get('autoDelayRepayMaxJourneys') - config.get('autoDelayRepayRemainingJourneysWarningThreshold')) && <div>
                                    {t('auto-dr.account.subscribe.journeys.modal.travel.selected', {
                                        journeysQuantity,
                                        maxJourneys: config.get('autoDelayRepayMaxJourneys'),
                                    })}
                                </div>}

                            </Col>
                            {canLoadLaterServices && <Col xs={2} className="journey-modal-travel-load-later">
                                <a
                                    onClick={loadLaterServices}
                                    href="#"
                                    className="service-pager pull-right"
                                    aria-label={t('booking.fare.eligibleServicesModal.table.later.ariaLabel')}>
                                    <span aria-hidden="true">
                                        <i className="otrl otrl-chevron otrl-rotate-90" aria-hidden="true" />
                                        <span className="sr-only">{t('booking.fare.eligibleServicesModal.table.show')}</span>
                                        {t('booking.fare.eligibleServicesModal.table.later')}
                                    </span>
                                </a>
                            </Col>}
                        </Row>
                    </Col>
                </div>
                <Row>
                    <Col xs={12} className="margin-bottom--sm margin-top--sm">
                        {journeysQuantity > maxJourneys && <BAlert type={ bAlertType.DANGER }>{t('auto-dr.subscribe.add-journey-max', { maxJourneys })}.</BAlert>}
                    </Col>
                </Row>
            </React.Fragment> : renderNoJourneySearchResults(loading)}
        </React.Fragment>
    );
};

AddJourneyModalTravelTable.propTypes = {
    businessDays: PropTypes.array.isRequired,
    journeys: PropTypes.array.isRequired,
    outwardJourneys: PropTypes.instanceOf(List).isRequired,
    remainingJourneys: PropTypes.number.isRequired,
    search: PropTypes.instanceOf(Search).isRequired,
    selectedJourneys: PropTypes.array.isRequired,
    setDisabledSaveJourney: PropTypes.func.isRequired,
    setNewTime: PropTypes.func.isRequired,
    setJourneyData: PropTypes.func.isRequired,
    setSelectedJourneys: PropTypes.func.isRequired,
    weekday: PropTypes.string.isRequired,
    editingJourney: PropTypes.instanceOf(List),
    loading: PropTypes.bool.isRequired,
};

function _getStateFromStores () {
    return {
        loading: LoadingStore.isLoading()
    };
}

export default connectToStores(
    [ LoadingStore ], _getStateFromStores
)(AddJourneyModalTravelTable);



// WEBPACK FOOTER //
// ./src/components/account/AddJourneyModalTravelTable.js